import _ from 'lodash';

export enum VendorName {
  UBER = 'UBER',
  LYFT = 'LYFT',
  INNISFIL = 'INNISFIL',
  ANY = 'ANY',
  STA = 'STA',
  NASH = 'NASH',
}

export enum RideLevel {
  car = 'car',
  suv = 'suv',
  van = 'van',
  premium = 'premium',
  lux = 'lux',
  luxsuv = 'luxsuv',
  wheelChair = 'wheelChair',
  espanol = 'espanol',
  assistance = 'assistance',
  delivery = 'delivery',
  other = 'other',
}

export enum RideTypeKey {
  car = 'car',
  uberX = 'uberX',
  lyft = 'lyft',
  suv = 'suv',
  uberXL = 'uberXL',
  uberXXL = 'UberXXL',
  lyftPlus = 'lyft_plus',
  premium = 'premium',
  comfort = 'Comfort',
  lux = 'lux',
  lyftLux = 'lyft_lux',
  luxsuv = 'luxsuv',
  lyftLuxsuv = 'lyft_luxsuv',
  uberSUV = 'UberSUV',
  black = 'Black',
  blackSUV = 'Black SUV',
  espanol = 'Espanol',
  wav = 'WAV',
  assist = 'assist',
  doorToDoor = 'Door to Door',
  innisfilTransit = 'Innisfil Transit',
  staWav = 'staWav',
  lyftAssisted = 'lyft_assisted',
  nash = 'nash',
}

export enum TaxiRideTypeKey {
  fresnoEOC = 'fresnoEOC',
  greenPoint = 'greenPoint',
}

export type RideTypeData = {
  partnerTaxiVendor?: {
    applySubsidyCondition?: boolean;
  };
};

export class RideType {
  constructor(
    public readonly key: string,
    public readonly level: RideLevel,
    public readonly vendor: VendorName,
    public readonly description: string,
    public readonly data?: RideTypeData,
  ) {}

  // car
  static readonly Car = new RideType(
    RideTypeKey.car,
    RideLevel.car,
    VendorName.ANY,
    'Car (Auto selects a vendor based on ETA or by user preferred)',
  );
  static readonly UberX = new RideType(RideTypeKey.uberX, RideLevel.car, VendorName.UBER, 'UberX (4 seats)');
  static readonly Lyft = new RideType(RideTypeKey.lyft, RideLevel.car, VendorName.LYFT, 'Lyft (4 seats)');

  // suv
  static readonly Suv = new RideType(
    RideTypeKey.suv,
    RideLevel.suv,
    VendorName.ANY,
    'SUV (Auto selects a vendor based on ETA or by user preferred)',
  );
  static readonly UberXL = new RideType(RideTypeKey.uberXL, RideLevel.suv, VendorName.UBER, 'UberXL (6 seats)');
  static readonly LyftPlus = new RideType(RideTypeKey.lyftPlus, RideLevel.suv, VendorName.LYFT, 'Lyft XL (6 seats)');

  // premium
  static readonly Premium = new RideType(
    RideTypeKey.premium,
    RideLevel.premium,
    VendorName.ANY,
    'Premium Sedan (Auto selects a vendor based on ETA or by user preferred)',
  );
  static readonly UberComfort = new RideType(RideTypeKey.comfort, RideLevel.premium, VendorName.UBER, 'Uber Comfort (4 seats)');

  // lux
  static readonly Lux = new RideType(
    RideTypeKey.lux,
    RideLevel.lux,
    VendorName.ANY,
    'Luxury Sedan (Auto selects a vendor based on ETA or by user preferred)',
  );
  static readonly UberBlack = new RideType(RideTypeKey.black, RideLevel.lux, VendorName.UBER, 'Uber Premier (commercially registered)');
  static readonly LyftLux = new RideType(RideTypeKey.lyftLux, RideLevel.lux, VendorName.LYFT, 'Lyft Lux (professional drivers)');

  // luxsuv
  static readonly Luxsuv = new RideType(
    RideTypeKey.luxsuv,
    RideLevel.luxsuv,
    VendorName.ANY,
    'Luxury SUV (Auto selects a vendor based on ETA or by user preferred)',
  );
  static readonly UberSUV = new RideType(RideTypeKey.uberSUV, RideLevel.luxsuv, VendorName.UBER, 'Uber Premier SUV (6 seats)');
  static readonly LyftLuxsuv = new RideType(RideTypeKey.lyftLuxsuv, RideLevel.luxsuv, VendorName.LYFT, 'Lyft Lux Black XL (6 seats)');
  static readonly UberBlackSuv = new RideType(
    RideTypeKey.blackSUV,
    RideLevel.luxsuv,
    VendorName.UBER,
    'Uber Premier SUV (commercially registered, 6 seats)',
  );

  // van
  static readonly UberXXL = new RideType(RideTypeKey.uberXXL, RideLevel.van, VendorName.UBER, 'UberXXL (6 seats)');

  // wheelchair
  static readonly WAV = new RideType(
    RideTypeKey.wav,
    RideLevel.wheelChair,
    VendorName.UBER,
    'Uber WAV (Wheelchair Accessible Vehicle, where available)',
  );
  static readonly Stawav = new RideType(RideTypeKey.staWav, RideLevel.wheelChair, VendorName.STA, 'Stawav');

  // assist
  static readonly UberAssist = new RideType(
    RideTypeKey.assist,
    RideLevel.assistance,
    VendorName.UBER,
    'Uber Assist (Extra assistance from drivers)',
  );

  // Door to Door
  static readonly UberDoorToDoor = new RideType(
    RideTypeKey.doorToDoor,
    RideLevel.car,
    VendorName.UBER,
    'Uber Door to Door (Assistance from door to door by NEMT-certified drivers)',
  );

  // lyft_assisted
  static readonly LyftAssisted = new RideType(
    RideTypeKey.lyftAssisted,
    RideLevel.assistance,
    VendorName.LYFT,
    'Lyft Assist (Extra assistance from drivers)',
  );

  // espanol
  static readonly Espanol = new RideType(RideTypeKey.espanol, RideLevel.espanol, VendorName.UBER, 'Uber Spanish speaking driver');

  // Delivery
  static readonly Nash = new RideType(RideTypeKey.nash, RideLevel.delivery, VendorName.NASH, 'Delivery');

  // other
  static readonly InnisfilTransit = new RideType(
    RideTypeKey.innisfilTransit,
    RideLevel.other,
    VendorName.INNISFIL,
    'Innisfil Transit (For users that are a part of Innisfil Transit)',
  );

  static readonly ALL = [
    this.Car,
    this.UberX,
    this.Lyft,
    this.Suv,
    this.UberXL,
    this.UberXXL,
    this.LyftPlus,
    this.Premium,
    this.UberComfort,
    this.Lux,
    this.LyftLux,
    this.Luxsuv,
    this.LyftLuxsuv,
    this.UberSUV,
    this.UberBlack,
    this.UberBlackSuv,
    this.Espanol,
    this.WAV,
    this.LyftAssisted,
    this.Nash,
  ];

  static readonly ALL_WITH_ASSIST = [...this.ALL, this.UberAssist, this.LyftAssisted, this.UberDoorToDoor];

  static readonly findByKey = _.memoize((key: string): RideType | undefined =>
    this.ALL_WITH_ASSIST.find((x) => x.key.toLowerCase() === key.toLowerCase()),
  );
}
